/**
 * SpreadsheetImport: add people to a campaign from a CSV or Excel file (wizard step 2 and the Lists tab's
 * "Add more people").
 *
 * 1. Pick a file (parsed in the browser with SheetJS) or download the template.
 * 2. Map the columns (auto-mapped from the header row) to first name, surname, full name, phone, email and note.
 * 3. Preview, then upload: startImport, then addImportRows in chunks of at most 500 (isLastChunk on the last one)
 *    with a progress bar. A 503 member_service_unavailable is retried twice by itself, then the upload pauses
 *    with a "Try again" button that carries on from the chunk that failed.
 * When the last chunk is in, the server has matched everyone to CCMS members and `onDone` gets the batch.
 *
 * Stage 6: "Suggest mapping" (importAssist on, Import right) sends the headers and up to 5 raw rows; the server sends
 * the model only the header text and value shapes. Suggestions fill only the columns still "Not in the file" (never
 * one the admin chose, never a column already used), with the confidence as a badge and the reasons as hints. The
 * admin checks and uploads as usual.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { IconDownload, IconUpload, IconFileSpreadsheet, IconAlertTriangle, IconRefresh, IconCheck, IconSparkles } from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Stack, Group, Button, Text, Select, Switch, Alert, Progress, Badge, Table, SimpleGrid, TextInput, Paper, ScrollArea } from '@/components/ui';
import { FileInput } from '@/components/ui/inputs';
import { useCallCentreAdminService, CALL_CENTRE_LIST_SOURCE_TYPE, type CallCentreImportBatchView, type CallCentreAiImportMapResult } from '@actschurch/ccms-sdk';
import { useCallCentrePermissions } from '@/hooks/callcentre/useCallCentrePermissions';
import { toast } from '@/components/ui/providers/AlertProvider';
import {
  CALL_LIST_FIELDS,
  EMPTY_CALL_LIST_MAPPING,
  NOT_MAPPED,
  buildCallListRows,
  downloadCallListTemplate,
  guessCallListMapping,
  looksLikeCallListHeader,
  parseSpreadsheetFile,
  type CallListField,
  type CallListMapping,
} from './callCentreImport';
import { CALL_CENTRE_AI_FAILED_TEXT, callCentreAiErrorKind, useCallCentreAiAvailability, useCallCentreAiUnavailable } from './useCallCentreAi';
import { CALL_CENTRE_LIMITS, callCentreErrorMessage, callCentreKeys, chunk, isMemberServiceUnavailable, plural } from './callCentreUtils';

interface SpreadsheetImportProps {
  campaignId: number;
  onDone: (batch: CallCentreImportBatchView) => void;
  onCancel?: () => void;
  /** data-help-id prefix for the tour anchors ("callcentre.campaign-wizard"). */
  helpPrefix?: string;
  /** Told when an upload starts (true) and when the form is back to picking (false), so a parent can lock other choices. */
  onBusyChange?: (busy: boolean) => void;
}

type Phase = 'pick' | 'uploading' | 'paused' | 'done';

/** What the AI suggested for one field: the column, how sure it is and why. */
interface AiFieldHint {
  col: string;
  header: string;
  confidence: number;
  reason?: string | null;
}

const AI_SAMPLE_ROWS = 5;
const CALL_LIST_FIELD_KEYS = new Set<string>(CALL_LIST_FIELDS.map((f) => f.field));

const confidenceColour = (c: number) => (c >= 80 ? 'green' : c >= 50 ? 'yellow' : 'orange');

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export function SpreadsheetImport({ campaignId, onDone, onCancel, helpPrefix, onBusyChange }: SpreadsheetImportProps) {
  const admin = useCallCentreAdminService();
  const queryClient = useQueryClient();
  const [fileName, setFileName] = useState('');
  const [sheet, setSheet] = useState<string[][]>([]);
  const [hasHeader, setHasHeader] = useState(true);
  const [mapping, setMapping] = useState<CallListMapping>(EMPTY_CALL_LIST_MAPPING);
  const [note, setNote] = useState('');
  const [parseError, setParseError] = useState<string | null>(null);
  const [phase, setPhase] = useState<Phase>('pick');
  const [sent, setSent] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [retrying, setRetrying] = useState(false);
  const batchIdRef = useRef<number | null>(null);
  const chunksRef = useRef<ReturnType<typeof buildCallListRows>[]>([]);
  const nextChunkRef = useRef(0);
  const perms = useCallCentrePermissions();
  const aiAvailability = useCallCentreAiAvailability();
  const aiOff = useCallCentreAiUnavailable();
  const [aiHints, setAiHints] = useState<Partial<Record<CallListField, AiFieldHint>>>({});
  const [aiApplied, setAiApplied] = useState<Partial<Record<CallListField, string>>>({});
  const [aiNotes, setAiNotes] = useState<string[]>([]);
  const [aiError, setAiError] = useState<string | null>(null);

  useEffect(() => {
    onBusyChange?.(phase !== 'pick');
  }, [phase, onBusyChange]);

  const headers = useMemo(() => (hasHeader && sheet[0] ? sheet[0].map((c, i) => String(c || `Column ${i + 1}`)) : []), [hasHeader, sheet]);
  const width = useMemo(() => Math.max(0, ...sheet.map((r) => r.length)), [sheet]);
  const columnOptions = useMemo(
    () => [
      { value: NOT_MAPPED, label: 'Not in the file' },
      ...Array.from({ length: width }, (_, i) => ({ value: String(i), label: headers[i] ? `${headers[i]}` : `Column ${i + 1}` })),
    ],
    [width, headers]
  );

  const parsed = useMemo(() => {
    if (sheet.length === 0) return [];
    const data = hasHeader ? sheet.slice(1) : sheet;
    return buildCallListRows(data, mapping, hasHeader ? 2 : 1, hasHeader ? headers : []);
  }, [sheet, hasHeader, mapping, headers]);

  const missingPhone = parsed.filter((p) => p.missingPhone).length;
  const missingName = parsed.filter((p) => p.missingName).length;
  const nameMapped = mapping.firstName !== NOT_MAPPED || mapping.fullName !== NOT_MAPPED;
  const phoneMapped = mapping.phone !== NOT_MAPPED;
  const tooMany = parsed.length > CALL_CENTRE_LIMITS.ImportRowsPerBatch;
  const canUpload = parsed.length > 0 && nameMapped && phoneMapped && !tooMany && phase === 'pick';

  const resetAi = () => {
    setAiHints({});
    setAiApplied({});
    setAiNotes([]);
    setAiError(null);
  };

  /** The headers as sent (the header row, or "Column 1".. without one) and up to 5 raw data rows in that order. */
  const aiRequest = () => {
    const sent = Array.from({ length: width }, (_, i) => (hasHeader ? String(sheet[0]?.[i] ?? '').trim() : '') || `Column ${i + 1}`);
    const rows = (hasHeader ? sheet.slice(1) : sheet)
      .filter((r) => r.some((c) => String(c ?? '').trim()))
      .slice(0, AI_SAMPLE_ROWS)
      .map((r) => sent.map((_, i) => {
        const v = String(r[i] ?? '').trim();
        return v || null;
      }));
    return { headers: sent, sampleRows: rows };
  };

  /** Fill only fields still "Not in the file" with a column nobody uses yet; remember every hint for the badges. */
  const applyAi = (result: CallCentreAiImportMapResult, sent: string[]) => {
    const used = new Set<number>();
    const best: Partial<Record<CallListField, AiFieldHint>> = {};
    for (const m of result.mappings ?? []) {
      const field = m.field ?? '';
      if (!CALL_LIST_FIELD_KEYS.has(field)) continue;
      const header = (m.header ?? '').trim();
      let idx = sent.findIndex((h, i) => !used.has(i) && h === header);
      if (idx < 0) idx = sent.findIndex((h, i) => !used.has(i) && h.toLowerCase() === header.toLowerCase());
      if (idx < 0) continue;
      used.add(idx);
      const hint: AiFieldHint = { col: String(idx), header: sent[idx], confidence: Math.max(0, Math.min(100, Math.round(m.confidence ?? 0))), reason: m.reason };
      const f = field as CallListField;
      if (!best[f] || hint.confidence > (best[f] as AiFieldHint).confidence) best[f] = hint;
    }
    const next: CallListMapping = { ...mapping };
    const taken = new Set(Object.values(next).filter((c) => c !== NOT_MAPPED));
    const applied: Partial<Record<CallListField, string>> = {};
    for (const def of CALL_LIST_FIELDS) {
      const hint = best[def.field];
      if (!hint || next[def.field] !== NOT_MAPPED || taken.has(hint.col)) continue;
      next[def.field] = hint.col;
      taken.add(hint.col);
      applied[def.field] = hint.col;
    }
    setMapping(next);
    setAiHints(best);
    setAiApplied(applied);
    setAiNotes((result.notes ?? []).filter((n) => !!n && n.trim()));
    const count = Object.keys(applied).length;
    toast.success(
      count ? `Filled in ${plural(count, 'column')}` : 'Nothing new to fill in',
      count ? 'Check the suggestions, then upload as usual.' : 'Your choices stay as they are. The hints show what the AI thought.'
    );
  };

  const suggestMapping = useMutation({
    mutationFn: (req: { headers: string[]; sampleRows: (string | null)[][] }) => admin.aiImportMap(req),
    onMutate: () => setAiError(null),
    onSuccess: (result, req) => applyAi(result, req.headers),
    onError: (err) => {
      if (callCentreAiErrorKind(err) === 'unavailable') {
        aiOff();
        return;
      }
      setAiError(CALL_CENTRE_AI_FAILED_TEXT);
      toast.error("Couldn't suggest a mapping", CALL_CENTRE_AI_FAILED_TEXT);
    },
  });

  const handleFile = async (file: File | null) => {
    setParseError(null);
    resetAi();
    if (!file) return;
    try {
      const rows = await parseSpreadsheetFile(file);
      if (rows.length === 0) {
        setParseError('That file has no rows. Check it is the right sheet.');
        return;
      }
      const header = looksLikeCallListHeader(rows[0]);
      setFileName(file.name);
      setSheet(rows);
      setHasHeader(header);
      setMapping(header ? guessCallListMapping(rows[0]) : { ...EMPTY_CALL_LIST_MAPPING, fullName: '0', phone: rows[0].length > 1 ? '1' : NOT_MAPPED });
    } catch {
      setParseError("That file couldn't be read. Use a CSV or Excel (XLSX) file.");
    }
  };

  const toggleHeader = (value: boolean) => {
    resetAi();
    setHasHeader(value);
    if (value && sheet[0]) setMapping(guessCallListMapping(sheet[0]));
  };

  /** Send the chunks from `from` onwards; a 503 is retried twice before pausing. */
  const sendFrom = async (from: number) => {
    setPhase('uploading');
    setUploadError(null);
    const chunks = chunksRef.current;
    for (let i = from; i < chunks.length; i++) {
      nextChunkRef.current = i;
      let attempt = 0;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        try {
          const batch = await admin.addImportRows(batchIdRef.current as number, {
            rows: chunks[i].map((p) => p.row),
            isLastChunk: i === chunks.length - 1,
          });
          setSent((n) => n + chunks[i].length);
          if (i === chunks.length - 1) {
            setPhase('done');
            queryClient.invalidateQueries({ queryKey: callCentreKeys.campaignImports(campaignId) });
            queryClient.setQueryData(callCentreKeys.importBatch(batch.importBatchID), batch);
            toast.success('List uploaded', `${plural(batch.totalRows, 'row')} checked against CCMS members. Review the matches next.`);
            onDone(batch);
          }
          break;
        } catch (err) {
          if (isMemberServiceUnavailable(err) && attempt < 2) {
            attempt++;
            setRetrying(true);
            await sleep(attempt === 1 ? 2000 : 5000);
            setRetrying(false);
            continue;
          }
          setRetrying(false);
          setPhase('paused');
          setUploadError(callCentreErrorMessage(err, "The upload stopped part of the way."));
          toast.error('The upload stopped', callCentreErrorMessage(err));
          return;
        }
      }
    }
  };

  const start = async () => {
    if (!canUpload) return;
    setSent(0);
    chunksRef.current = chunk(parsed, CALL_CENTRE_LIMITS.ImportRowsPerRequest);
    nextChunkRef.current = 0;
    setPhase('uploading');
    setUploadError(null);
    let attempt = 0;
    // eslint-disable-next-line no-constant-condition
    while (true) {
      try {
        const batch = await admin.startImport(campaignId, {
          sourceTypeID: CALL_CENTRE_LIST_SOURCE_TYPE.Spreadsheet,
          fileName: fileName || null,
          note: note.trim() || null,
        });
        batchIdRef.current = batch.importBatchID;
        break;
      } catch (err) {
        if (isMemberServiceUnavailable(err) && attempt < 2) {
          attempt++;
          setRetrying(true);
          await sleep(attempt === 1 ? 2000 : 5000);
          setRetrying(false);
          continue;
        }
        setRetrying(false);
        setPhase('pick');
        setUploadError(callCentreErrorMessage(err, "The list couldn't be started."));
        toast.error("Couldn't start the upload", callCentreErrorMessage(err));
        return;
      }
    }
    await sendFrom(0);
  };

  const resume = async () => {
    if (batchIdRef.current == null) {
      await start();
      return;
    }
    await sendFrom(nextChunkRef.current);
  };

  const total = parsed.length;
  const percent = total > 0 ? Math.round((sent / total) * 100) : 0;
  const busy = phase === 'uploading';
  const locked = phase !== 'pick';
  const showAiMap = aiAvailability.importAssist && perms.canImport && width > 0 && width <= 60;

  return (
    <Stack gap="md" data-help-id={helpPrefix ? `${helpPrefix}.spreadsheet` : undefined}>
      <Group justify="space-between" wrap="wrap" gap="sm">
        <Text size="sm" c="dimmed" maw={560}>
          Upload a CSV or Excel file with one person per row. You need a name and a phone number; email helps us find them in CCMS.
        </Text>
        <Group gap="xs" data-help-id={helpPrefix ? `${helpPrefix}.template` : undefined}>
          <Button size="xs" variant="light" leftSection={<IconDownload size={14} />} onClick={() => downloadCallListTemplate('csv')}>
            CSV template
          </Button>
          <Button size="xs" variant="subtle" leftSection={<IconDownload size={14} />} onClick={() => downloadCallListTemplate('xlsx')}>
            Excel template
          </Button>
        </Group>
      </Group>

      <Group gap="md" align="flex-end" wrap="wrap">
        <FileInput
          label="Spreadsheet"
          placeholder={fileName || 'Choose a CSV or XLSX file'}
          accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
          leftSection={<IconFileSpreadsheet size={16} />}
          onChange={(f: File | null) => void handleFile(f)}
          disabled={locked}
          clearable={false}
          style={{ flex: 1, minWidth: 260 }}
        />
        {sheet.length > 0 && <Switch label="First row is a header" checked={hasHeader} onChange={(e) => toggleHeader(e.currentTarget.checked)} disabled={locked} />}
      </Group>
      {parseError && <Alert color="red">{parseError}</Alert>}

      {sheet.length > 0 && (
        <Paper withBorder radius="md" p="md" data-help-id={helpPrefix ? `${helpPrefix}.mapping` : undefined}>
          <Stack gap="sm">
            <Group justify="space-between" wrap="wrap" gap="xs">
              <Text size="sm" fw={600}>
                Match the columns
              </Text>
              {showAiMap && (
                <Button
                  size="compact-sm"
                  variant="light"
                  color="violet"
                  leftSection={<IconSparkles size={14} />}
                  loading={suggestMapping.isPending}
                  disabled={locked}
                  onClick={() => suggestMapping.mutate(aiRequest())}
                  data-help-id={helpPrefix ? `${helpPrefix}.ai-mapping` : undefined}
                >
                  Suggest mapping
                </Button>
              )}
            </Group>
            <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="sm">
              {CALL_LIST_FIELDS.map((f) => {
                const hint = aiHints[f.field];
                const applied = !!hint && aiApplied[f.field] === mapping[f.field] && mapping[f.field] === hint.col;
                const differs = !!hint && !applied && mapping[f.field] !== hint.col;
                return (
                  <Stack key={f.field} gap={4}>
                    <Select
                      label={f.label}
                      description={f.hint}
                      data={columnOptions}
                      value={mapping[f.field]}
                      onChange={(v) => setMapping((m) => ({ ...m, [f.field]: v ?? NOT_MAPPED }))}
                      allowDeselect={false}
                      disabled={locked}
                    />
                    {applied && hint && (
                      <Group gap={6} wrap="nowrap" align="flex-start">
                        <Badge size="xs" variant="light" color={confidenceColour(hint.confidence)} styles={{ root: { textTransform: 'none', flexShrink: 0 } }}>
                          AI {hint.confidence}%
                        </Badge>
                        {hint.reason && (
                          <Text size="xs" c="dimmed">
                            {hint.reason}
                          </Text>
                        )}
                      </Group>
                    )}
                    {differs && hint && (
                      <Text size="xs" c="dimmed">
                        AI suggests {hint.header} ({hint.confidence}%). Your choice stays.
                      </Text>
                    )}
                  </Stack>
                );
              })}
            </SimpleGrid>
            {(aiNotes.length > 0 || Object.keys(aiHints).length > 0) && (
              <Alert color="violet" icon={<IconSparkles size={16} />} p="xs">
                <Stack gap={4}>
                  {aiNotes.map((n, i) => (
                    <Text key={i} size="xs">
                      {n}
                    </Text>
                  ))}
                  <Text size="xs" c="dimmed">
                    Suggested by AI from the column names and the shape of the first rows. Check the columns before you upload.
                  </Text>
                </Stack>
              </Alert>
            )}
            {aiError && (
              <Alert color="orange" p="xs">
                <Text size="sm">{aiError}</Text>
              </Alert>
            )}
            {!nameMapped && <Alert color="orange">Choose the First name column or the Full name column.</Alert>}
            {!phoneMapped && <Alert color="orange">Choose the Phone column. People without a phone number can't be called.</Alert>}
          </Stack>
        </Paper>
      )}

      {parsed.length > 0 && (
        <Paper withBorder radius="md" p="md" data-help-id={helpPrefix ? `${helpPrefix}.preview` : undefined}>
          <Stack gap="sm">
            <Group gap="xs" wrap="wrap">
              <Badge variant="light" color="blue" styles={{ root: { textTransform: 'none' } }}>
                {plural(parsed.length, 'person', 'people')}
              </Badge>
              {missingPhone > 0 && (
                <Badge variant="light" color="orange" styles={{ root: { textTransform: 'none' } }}>
                  {missingPhone} without a phone
                </Badge>
              )}
              {missingName > 0 && (
                <Badge variant="light" color="orange" styles={{ root: { textTransform: 'none' } }}>
                  {missingName} without a name
                </Badge>
              )}
            </Group>
            {tooMany && (
              <Alert color="red">
                One list can hold at most {CALL_CENTRE_LIMITS.ImportRowsPerBatch.toLocaleString('en-ZA')} people. Split the file and upload the parts one after the other.
              </Alert>
            )}
            {(missingPhone > 0 || missingName > 0) && (
              <Text size="xs" c="dimmed">
                Rows without a usable phone number are marked Invalid on the next step and are not added.
              </Text>
            )}
            <ScrollArea>
              <Table tableVariant="striped" fz="xs" verticalSpacing={4}>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Row</Table.Th>
                    <Table.Th>Name</Table.Th>
                    <Table.Th>Phone</Table.Th>
                    <Table.Th>Email</Table.Th>
                    <Table.Th>Note</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {parsed.slice(0, 8).map((p) => (
                    <Table.Tr key={p.row.rowNumber}>
                      <Table.Td>{p.row.rowNumber}</Table.Td>
                      <Table.Td>{p.row.fullName || [p.row.firstName, p.row.lastName].filter(Boolean).join(' ') || <Text span size="xs" c="orange">No name</Text>}</Table.Td>
                      <Table.Td>{p.row.phone || <Text span size="xs" c="orange">No phone</Text>}</Table.Td>
                      <Table.Td>{p.row.email}</Table.Td>
                      <Table.Td>{p.row.note}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </ScrollArea>
            {parsed.length > 8 && (
              <Text size="xs" c="dimmed">
                Showing the first 8 of {parsed.length} rows.
              </Text>
            )}
            <TextInput label="Note about this list (optional)" placeholder="For example: Connect cards, 12 October" value={note} onChange={(e) => setNote(e.currentTarget.value)} maxLength={500} disabled={locked} />
          </Stack>
        </Paper>
      )}

      {phase !== 'pick' && (
        <Paper withBorder radius="md" p="md" data-help-id={helpPrefix ? `${helpPrefix}.progress` : undefined}>
          <Stack gap="xs">
            <Group justify="space-between">
              <Text size="sm" fw={600}>
                {phase === 'done' ? 'Uploaded and checked' : retrying ? 'The member service is busy, trying again' : phase === 'paused' ? 'Upload paused' : 'Uploading and matching'}
              </Text>
              <Text size="sm" c="dimmed">
                {sent} of {total}
              </Text>
            </Group>
            <Progress value={percent} animated={busy} color={phase === 'paused' ? 'orange' : phase === 'done' ? 'green' : 'blue'} />
          </Stack>
        </Paper>
      )}

      {uploadError && (
        <Alert color={phase === 'paused' ? 'orange' : 'red'} icon={<IconAlertTriangle size={16} />}>
          <Stack gap="xs">
            <Text size="sm">{uploadError}</Text>
            {phase === 'paused' && (
              <Text size="xs" c="dimmed">
                The rows sent so far are kept. Try again to carry on from where it stopped.
              </Text>
            )}
            <Group>
              <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />} onClick={() => void (phase === 'paused' ? resume() : start())}>
                Try again
              </Button>
            </Group>
          </Stack>
        </Alert>
      )}

      <Group justify="flex-end" gap="sm">
        {onCancel && phase === 'pick' && (
          <Button variant="subtle" onClick={onCancel}>
            Cancel
          </Button>
        )}
        {phase === 'done' ? (
          <Badge color="green" variant="light" leftSection={<IconCheck size={12} />} styles={{ root: { textTransform: 'none' } }}>
            Done
          </Badge>
        ) : (
          <Button leftSection={<IconUpload size={16} />} disabled={!canUpload} loading={busy} onClick={() => void start()} data-help-id={helpPrefix ? `${helpPrefix}.upload` : undefined}>
            {parsed.length > 0 ? `Upload ${plural(parsed.length, 'person', 'people')}` : 'Upload'}
          </Button>
        )}
      </Group>
    </Stack>
  );
}
